// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { LENGTH_SOURCE_ORDER } from './holeLengthRows';
import type { CourseTee } from './useCourseCard';
import {
  UNKNOWN_SOURCE,
  type CourseHole,
  type CourseHoleCard,
  type CourseHoleLength,
  type CourseHoleTeeLength,
} from './useCourseHoles';

/**
 * The whole course as one card: holes down the side, tees across the top.
 *
 * {@link buildHoleLengthTable} answers "do the sources agree about this hole"; this answers
 * "what does the card say", which is the question an admin opens a course with. There is one
 * column per tee and so no room for a column per source, so each cell names the source its
 * number came from — the same admin-review use CLAUDE.md §19 keeps card lengths around for.
 * Nothing here is what a player is shown; the server decides that on its own.
 */

/** The key of the column holding lengths measured with no tee in mind. */
export const HOLE_COLUMN_KEY = 'hole';

/** One column: a tee, or the hole's own tee-less lengths. */
export type CourseCardColumn = {
  /** The tee's id, or {@link HOLE_COLUMN_KEY}. */
  key: string;
  /** The tee, or null for the tee-less column, which the caller names. */
  tee: CourseTee | null;
  /**
   * The source an admin can make official for this column, or null when there is nothing to
   * adopt — see {@link adoptableSource}.
   */
  adoptableSource: string | null;
};

/** One measured cell: which number, and who measured it. */
export type CourseCardCell = { lengthMeters: number; source: string } | null;

/**
 * The best-attested stated card for one hole, and whether it disagrees with the stored row.
 *
 * `differs` is the reason this exists. A club's card is recorded even where CLAUDE.md §18 keeps
 * it off the hole, so the hole can show an invented par indefinitely with the real one sitting
 * one table away and nothing on screen saying so. Orust GK read as eighteen par 4s with stroke
 * indexes 1…18 for exactly that reason.
 */
export type CourseCardStated = {
  source: string;
  par: number;
  strokeIndex: number;
  /** True when this card and the hole's propagated par or stroke index are not the same. */
  differs: boolean;
} | null;

export type CourseCardRow = {
  number: number;
  par: number | null;
  strokeIndex: number | null;
  /** One entry per column, in {@link CourseCardTable.columns} order. */
  cells: CourseCardCell[];
  /** What a source says this hole's card is, or null when none has said. */
  stated: CourseCardStated;
};

export type CourseCardTable = {
  columns: CourseCardColumn[];
  rows: CourseCardRow[];
  /** Par summed over the holes that have one, and null when none does. */
  parTotal: number | null;
  /**
   * Per column: the sum, but only where every hole has a number. A partial sum reads as a
   * course length and would be one hole short of it, which is worse than no total at all.
   */
  columnTotals: (number | null)[];
  /** The sources that actually contributed a cell, best-attested first. */
  sources: string[];
  /**
   * The sources that stated a card, best-attested first. Empty until an import records one,
   * which is every course on a catalogue that predates per-source cards.
   */
  cardSources: string[];
  /** Holes whose stated card disagrees with the par or stroke index stored on the hole. */
  differingHoles: number;
  /** The source an admin can make official for the card column, or null — see {@link adoptableSource}. */
  cardAdoptableSource: string | null;
};

const rank = (source: string) => {
  const index = LENGTH_SOURCE_ORDER.indexOf(source);
  return index < 0 ? LENGTH_SOURCE_ORDER.length : index;
};

/**
 * Card sources, best-attested first — deliberately not {@link LENGTH_SOURCE_ORDER}.
 *
 * A length ranks our own measurement above a person's, because we measured the geometry. A par
 * is not measured: it is stated, so the person who plays the course outranks a scrape of a
 * website, which is the order golf-courses-service's `HoleCardSelector` uses. There is no
 * "analysis" here at all — analysis derives no par.
 */
export const CARD_SOURCE_ORDER = ['manual', 'golfisverige', 'golfingholiday', 'osm', UNKNOWN_SOURCE];

const cardRank = (source: string) => {
  const index = CARD_SOURCE_ORDER.indexOf(source);
  return index < 0 ? CARD_SOURCE_ORDER.length : index;
};

/** Sources a player is already shown a length from: our own measurement, and a person's. */
const OFFICIAL_LENGTH_SOURCES: readonly string[] = ['analysis', 'manual'];

/** The one source whose stated par and stroke index are the hole's own. */
const OFFICIAL_CARD_SOURCES: readonly string[] = ['manual'];

/**
 * The source the "Use …" button under a column offers, or null when the column has nothing to
 * offer.
 *
 * The best-attested source among the numbers the column actually shows, skipping the ones that
 * are already official. A button to adopt what a player already sees would do nothing, and one
 * that adopted a number the column does not show would adopt something the admin never looked
 * at. Pressing it is the human decision CLAUDE.md §18 and §19 defer to: the server copies that
 * source's numbers into the manual row, as if the admin had typed them in.
 */
const adoptableSource = (
  shown: readonly string[],
  official: readonly string[],
  rankOf: (source: string) => number
): string | null => {
  let chosen: string | null = null;
  for (const source of shown) {
    if (official.includes(source)) continue;
    if (chosen === null || rankOf(source) < rankOf(chosen)) chosen = source;
  }
  return chosen;
};

/**
 * The best-attested of several numbers for one tee on one hole.
 *
 * Our own measurement first, then a person, then the club's card, then OSM — the same order
 * {@link buildHoleLengthTable} lays its columns out in. An unrecognised source sorts last but
 * is still used: it is a length somebody stored, and showing nothing would claim nobody has.
 */
const best = (
  lengths: readonly (CourseHoleLength | CourseHoleTeeLength)[]
): CourseCardCell => {
  let chosen: CourseHoleLength | CourseHoleTeeLength | null = null;
  for (const length of lengths) {
    if (chosen === null || rank(length.source) < rank(chosen.source)) chosen = length;
  }
  return chosen ? { lengthMeters: chosen.lengthMeters, source: chosen.source } : null;
};

/**
 * The best-attested card among those stated for one hole.
 *
 * Ranked by {@link CARD_SOURCE_ORDER}, which is golf-courses-service's `HoleCardSelector` order:
 * a person outranks a club's card outranks an OSM tag. An unrecognised source sorts last and is
 * still used, for the reason {@link best} keeps one — somebody stored it.
 */
const bestCard = (
  cards: readonly CourseHoleCard[],
  par: number | null,
  strokeIndex: number | null
): CourseCardStated => {
  let chosen: CourseHoleCard | null = null;
  for (const card of cards) {
    if (chosen === null || cardRank(card.source) < cardRank(chosen.source)) chosen = card;
  }
  if (chosen === null) return null;
  return {
    source: chosen.source,
    par: chosen.par,
    strokeIndex: chosen.strokeIndex,
    // A hole with no stored par cannot disagree with anything: it has no claim to contradict,
    // and marking it as a disagreement would send an admin looking for a conflict that is
    // really just an empty row.
    differs:
      (par !== null && par !== chosen.par) ||
      (strokeIndex !== null && strokeIndex !== chosen.strokeIndex),
  };
};

/**
 * The card for one course, or null when there is nothing to lay out.
 *
 * The tee-less column is added only when some hole has a length measured without a tee — on a
 * course with tees it is the analysis or OSM number to compare them against, and on one
 * without it is the only length there is.
 */
export const buildCourseCardTable = (
  holes: readonly CourseHole[],
  tees: readonly CourseTee[]
): CourseCardTable | null => {
  if (holes.length === 0) return null;

  const bareColumns: Omit<CourseCardColumn, 'adoptableSource'>[] = tees.map((tee) => ({
    key: tee.id,
    tee,
  }));
  if (holes.some((hole) => hole.lengths.length > 0)) {
    bareColumns.push({ key: HOLE_COLUMN_KEY, tee: null });
  }

  const rows: CourseCardRow[] = holes.map((hole) => ({
    number: hole.number,
    par: hole.par,
    strokeIndex: hole.strokeIndex,
    cells: bareColumns.map((column) =>
      column.tee === null
        ? best(hole.lengths)
        : best(hole.teeLengths.filter((length) => length.teeId === column.tee!.id))
    ),
    stated: bestCard(hole.cards, hole.par, hole.strokeIndex),
  }));

  const columns: CourseCardColumn[] = bareColumns.map((column, index) => ({
    ...column,
    adoptableSource: adoptableSource(
      rows.flatMap((row) => {
        const cell = row.cells[index];
        return cell ? [cell.source] : [];
      }),
      OFFICIAL_LENGTH_SOURCES,
      rank
    ),
  }));

  const pars = holes.map((hole) => hole.par).filter((par): par is number => par !== null);
  const columnTotals = columns.map((_, index) =>
    rows.every((row) => row.cells[index] !== null)
      ? rows.reduce((sum, row) => sum + (row.cells[index]?.lengthMeters ?? 0), 0)
      : null
  );

  const sources = [
    ...new Set(
      rows.flatMap((row) =>
        row.cells.filter((cell): cell is { lengthMeters: number; source: string } => cell !== null)
          .map((cell) => cell.source)
      )
    ),
  ].sort((a, b) => (rank(a) === rank(b) ? a.localeCompare(b) : rank(a) - rank(b)));

  const cardSources = [
    ...new Set(holes.flatMap((hole) => hole.cards.map((card) => card.source))),
  ].sort((a, b) => (cardRank(a) === cardRank(b) ? a.localeCompare(b) : cardRank(a) - cardRank(b)));

  return {
    columns,
    rows,
    parTotal: pars.length > 0 ? pars.reduce((sum, par) => sum + par, 0) : null,
    columnTotals,
    sources,
    cardSources,
    differingHoles: rows.filter((row) => row.stated?.differs).length,
    cardAdoptableSource: adoptableSource(
      rows.flatMap((row) => (row.stated ? [row.stated.source] : [])),
      OFFICIAL_CARD_SOURCES,
      cardRank
    ),
  };
};
